import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, ShieldCheck, Leaf, Zap, Eye, ArrowRight, Activity, Beaker, Star, ChevronDown, XCircle, FlaskConical, Users, Award } from 'lucide-react';
export function SalesPage() {
  const navigate = useNavigate();
  const handleBuyNow = () => {
    navigate('/checkout');
  };
  // Graceful fallback so no image ever renders broken
  const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    img.style.display = 'none';
    const parent = img.parentElement;
    if (parent) {
      parent.classList.add('bg-gradient-to-br', 'from-[#A67B5B]', 'via-[#8B5A2B]', 'to-[#5C3A1A]');
    }
  };
  const CTAButton = ({
    text = 'Quiero recuperar mi cabello',
    subtext = ''
  }) => <div className="w-full max-w-xl mx-auto flex flex-col items-center" data-id="element-151">
      <button onClick={handleBuyNow} className="group relative w-full overflow-hidden bg-gradient-to-br from-[#A67B5B] via-[#8B5A2B] to-[#5C3A1A] hover:from-[#B8895F] hover:via-[#8B5A2B] hover:to-[#4A2F14] text-white text-xl md:text-2xl font-bold py-6 px-8 rounded-sm shadow-2xl shadow-[#8B5A2B]/40 transition-all flex items-center justify-center gap-3 tracking-wide" data-id="element-152">
        <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12" data-id="element-153" />
        <span className="relative flex items-center gap-3" data-id="element-154">
          {text} <ArrowRight className="w-6 h-6" data-id="element-155" />
        </span>
      </button>
      {subtext && <p className="mt-4 text-[#8C7A6B] text-sm tracking-wide font-medium" data-id="element-156">
          {subtext}
        </p>}
    </div>;
  return <div className="min-h-screen bg-[#FCFAF8] font-sans text-[#3E2723]" data-id="element-157">
      {/* Top Bar */}
      <div className="bg-[#2C1E16] text-[#E8DCC4] text-center py-3 text-xs md:text-sm font-bold tracking-[0.2em] uppercase" data-id="element-158">
        Ingredientes con Respaldo Clínico • 100% Vegano • Sin Hormonas
      </div>

      {/* Ambient glow behind hero */}
      <div className="relative" data-id="element-159">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[700px] bg-gradient-to-b from-[#F4EFEA] via-[#FCFAF8] to-transparent" data-id="element-160" />
        <div className="pointer-events-none absolute left-1/2 top-24 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#E8DCC4] blur-[120px] opacity-50" data-id="element-161" />
        {/* Botanical spa accents (decorative) */}
        <img src="https://images.unsplash.com/photo-1512428813834-c702c7702b78?auto=format&fit=crop&q=80&w=400&h=600" alt="" aria-hidden="true" onError={handleImgError} className="pointer-events-none absolute top-40 -left-10 w-40 md:w-56 opacity-[0.15] mix-blend-multiply rotate-12 rounded-full object-cover" data-id="element-162" />
        <img src="https://images.unsplash.com/photo-1466781783364-36c955e42a7f?auto=format&fit=crop&q=80&w=400&h=600" alt="" aria-hidden="true" onError={handleImgError} className="pointer-events-none absolute top-[520px] -right-10 w-40 md:w-56 opacity-[0.15] mix-blend-multiply -rotate-12 rounded-full object-cover" data-id="element-163" />

        <div className="relative max-w-4xl mx-auto px-4" data-id="element-164">
          {/* HERO SECTION */}
          <section className="py-16 md:py-24 text-center space-y-10" data-id="element-165">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#F4EFEA] text-[#8B5A2B] text-sm font-bold tracking-widest uppercase border border-[#E8DCC4]" data-id="element-166">
              <Activity className="w-4 h-4" data-id="element-167" />
              <span data-id="element-168">Fórmula Clínica Avanzada</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-serif leading-[1.1] text-[#2C1E16] font-bold" data-id="element-169">
              Es hora de dejar de camuflar la pérdida de cabello.{' '}
              <br className="hidden md:block" data-id="element-170" />
              <span className="text-[#8B5A2B] italic" data-id="element-171">
                Fortalécelo desde la raíz
              </span>{' '}
              y devuélvele su esplendor natural.
            </h1>

            <p className="text-xl md:text-2xl text-[#5C4033] leading-relaxed font-light max-w-3xl mx-auto" data-id="element-172">
              El único sistema de soporte celular 100% vegano y sin hormonas,
              formulado para atacar las causas de raíz de la caída y el
              debilitamiento del cabello.
            </p>

            <div className="pt-6" data-id="element-173">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-8 border-white min-h-[240px]" data-id="element-174">
                <img src="https://cdn.magicpatterns.com/uploads/tvHMKmKP3zYbPscufERnYK/minevithome.jpg" alt="Mujer de cabello largo, sano y abundante, luciendo su melena con confianza" onError={handleImgError} className="w-full object-cover object-center aspect-[16/9]" data-id="element-175" />
                {/* Warm gradient wash + shine */}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#2C1E16]/50 via-transparent to-white/20" data-id="element-176" />
                <div className="pointer-events-none absolute -top-1/2 left-0 w-full h-full bg-gradient-to-br from-white/40 to-transparent rotate-12 blur-2xl opacity-60" data-id="element-177" />
              </div>
            </div>

            <div className="pt-8" data-id="element-178">
              <CTAButton subtext="Pago seguro con cifrado de 256 bits. Garantía de devolución de 60 días." data-id="element-179" />
            </div>

            {/* SOCIAL PROOF / RATING BAR */}
            <div className="pt-4 flex flex-col items-center gap-3" data-id="element-180">
              <div className="flex items-center gap-1 text-[#8B5A2B]" data-id="element-181">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 fill-current" data-id="element-182" />)}
              </div>
              <p className="text-[#5C4033] text-base md:text-lg" data-id="element-183">
                <strong className="text-[#2C1E16]" data-id="element-184">4.9 / 5</strong> · más de{' '}
                <strong className="text-[#2C1E16]" data-id="element-185">12.000 mujeres</strong>{' '}
                confían en MINEvit para recuperar su cabello
              </p>
            </div>
          </section>

          {/* CREDIBILITY / BACKING BAND */}
          <section className="pb-16" data-id="element-186">
            <div className="bg-white rounded-2xl shadow-lg border border-[#E8DCC4] px-6 py-10 md:px-12" data-id="element-187">
              {/* Backing stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 text-center" data-id="element-188">
                <div className="space-y-1" data-id="element-189">
                  <p className="text-4xl md:text-5xl font-serif font-bold text-[#8B5A2B]" data-id="element-190">
                    93%
                  </p>
                  <p className="text-[#5C4033] font-light leading-snug" data-id="element-191">
                    notó un cabello visiblemente más fuerte a los 90 días de uso
                    constante
                  </p>
                </div>
                <div className="space-y-1 md:border-x md:border-[#E8DCC4]" data-id="element-192">
                  <p className="text-4xl md:text-5xl font-serif font-bold text-[#8B5A2B]" data-id="element-193">
                    12
                  </p>
                  <p className="text-[#5C4033] font-light leading-snug" data-id="element-194">
                    activos biodisponibles respaldados por evidencia para la
                    salud capilar
                  </p>
                </div>
                <div className="space-y-1" data-id="element-195">
                  <p className="text-4xl md:text-5xl font-serif font-bold text-[#8B5A2B]" data-id="element-196">
                    60 días
                  </p>
                  <p className="text-[#5C4033] font-light leading-snug" data-id="element-197">
                    de garantía total de devolución, sin preguntas ni riesgos
                  </p>
                </div>
              </div>

              {/* Trust badges */}
              <div className="mt-10 pt-8 border-t border-[#F4EFEA] grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-198">
                {[{
                icon: FlaskConical,
                label: 'Formulado en laboratorios certificados'
              }, {
                icon: Leaf,
                label: '100% vegano, sin hormonas ni rellenos'
              }, {
                icon: Award,
                label: 'Ingredientes con respaldo clínico'
              }, {
                icon: Users,
                label: 'Miles de reseñas verificadas'
              }].map((badge, i) => <div key={i} className="flex flex-col items-center text-center gap-3" data-id="element-199">
                    <div className="w-14 h-14 rounded-full bg-[#F4EFEA] border border-[#E8DCC4] flex items-center justify-center" data-id="element-200">
                      <badge.icon className="w-7 h-7 text-[#8B5A2B]" data-id="element-201" />
                    </div>
                    <p className="text-sm text-[#5C4033] leading-snug font-medium" data-id="element-202">
                      {badge.label}
                    </p>
                  </div>)}
              </div>
            </div>

            <p className="mt-4 text-center text-xs text-[#8C7A6B] font-light max-w-2xl mx-auto" data-id="element-203">
              Este producto es un suplemento alimenticio y no pretende
              diagnosticar, tratar ni curar ninguna enfermedad. Los resultados
              pueden variar de una persona a otra.
            </p>
          </section>

          {/* PROBLEM / AGITATION */}
          <section className="py-20 border-t border-[#E8DCC4]" data-id="element-204">
            <h2 className="text-4xl md:text-5xl font-serif text-[#2C1E16] mb-10 text-center font-bold" data-id="element-205">
              ¿Te identificas con esto?
            </h2>

            <div className="relative rounded-3xl overflow-hidden shadow-xl border-4 border-white mb-10 min-h-[200px]" data-id="element-206">
              <img src="https://cdn.magicpatterns.com/uploads/f5ydvjkXjFMXqecoQZJzwB/image1.webp" alt="Mujer revisando su cabello adelgazado frente al espejo, preocupada por la caída" onError={handleImgError} className="w-full object-cover object-center aspect-[16/9] md:aspect-[21/9]" data-id="element-207" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#2C1E16]/70 via-[#2C1E16]/10 to-transparent" data-id="element-208" />
              <p className="absolute bottom-4 left-6 right-6 text-white text-lg md:text-2xl font-serif italic drop-shadow-lg" data-id="element-209">
                El adelgazamiento, las entradas que se ensanchan y la caída no
                son solo estéticos — son una señal.
              </p>
            </div>

            <div className="bg-white p-10 md:p-16 rounded-sm shadow-lg border border-[#F4EFEA] space-y-8" data-id="element-210">
              <ul className="space-y-6 text-xl text-[#5C4033]" data-id="element-211">
                <li className="flex items-start gap-4" data-id="element-212">
                  <XCircle className="w-8 h-8 text-red-800/70 shrink-0 mt-1" data-id="element-213" />
                  <span data-id="element-214">
                    Estás cansada de encontrar mechones de cabello en la ducha
                    cada mañana.
                  </span>
                </li>
                <li className="flex items-start gap-4" data-id="element-215">
                  <XCircle className="w-8 h-8 text-red-800/70 shrink-0 mt-1" data-id="element-216" />
                  <span data-id="element-217">
                    Tu cabello se ve fino, sin volumen y sin la fuerza que antes
                    tenía.
                  </span>
                </li>
                <li className="flex items-start gap-4" data-id="element-218">
                  <XCircle className="w-8 h-8 text-red-800/70 shrink-0 mt-1" data-id="element-219" />
                  <span data-id="element-220">
                    Sientes que ningún champú, aceite ni tratamiento tópico
                    resuelve el problema desde la raíz.
                  </span>
                </li>
                <li className="flex items-start gap-4" data-id="element-221">
                  <XCircle className="w-8 h-8 text-red-800/70 shrink-0 mt-1" data-id="element-222" />
                  <span data-id="element-223">
                    Has probado "gomitas para el cabello" genéricas llenas de
                    azúcar y biotina barata, sin ver ningún resultado.
                  </span>
                </li>
              </ul>
              <div className="pt-8 border-t border-[#E8DCC4]" data-id="element-224">
                <p className="text-2xl font-serif text-[#2C1E16] leading-relaxed" data-id="element-225">
                  Si respondiste que sí a alguna de estas,{' '}
                  <strong className="text-[#8B5A2B]" data-id="element-226">no es tu culpa.</strong> La
                  mayoría de los suplementos usan rellenos sintéticos que tu
                  cuerpo ni siquiera absorbe. Necesitas una solución a nivel
                  celular.
                </p>
              </div>
            </div>
          </section>

          {/* MECHANISM / HOW IT WORKS */}
          <section className="py-20" data-id="element-227">
            <div className="text-center mb-16" data-id="element-228">
              <h2 className="text-4xl md:text-6xl font-serif text-[#2C1E16] mb-6 font-bold" data-id="element-229">
                La ciencia de la{' '}
                <span className="italic text-[#8B5A2B]" data-id="element-230">
                  regeneración celular
                </span>
              </h2>
              <p className="text-xl text-[#5C4033] font-light" data-id="element-231">
                MINEvit no enmascara los síntomas. Aporta exactamente los
                bloques de construcción biodisponibles que tu cuerpo necesita.
              </p>
            </div>

            <div className="relative rounded-3xl overflow-hidden shadow-xl mb-16 border-4 border-white min-h-[200px]" data-id="element-232">
              <img src="https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&q=80&w=1200&h=514" alt="Primer plano de cabello sano y denso creciendo desde la raíz" onError={handleImgError} className="w-full object-cover aspect-[21/9]" data-id="element-233" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-[#8B5A2B]/30 via-transparent to-white/25" data-id="element-234" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#2C1E16]/40 to-transparent" data-id="element-235" />
            </div>

            <div className="space-y-12" data-id="element-236">
              {[{
              title: 'Máxima biodisponibilidad',
              desc: 'Los ingredientes vienen pre-metilados, así evitan la destrucción digestiva y entran a tus células al instante.',
              icon: Beaker
            }, {
              title: 'Mezcla sinérgica',
              desc: 'Las vitaminas trabajan en conjunto, no de forma aislada. Nuestra fórmula equilibra las rutas internas para optimizar la absorción.',
              icon: Activity
            }, {
              title: 'Cero rellenos ni toxinas',
              desc: 'Sin colorantes artificiales, aglutinantes, lácteos, gluten ni conservadores sintéticos. Solo eficacia pura 100% vegana.',
              icon: Leaf
            }].map((item, i) => <div key={i} className="flex flex-col md:flex-row items-center md:items-start gap-6 text-center md:text-left" data-id="element-237">
                  <div className="bg-[#F4EFEA] w-20 h-20 rounded-full flex items-center justify-center shrink-0 border border-[#E8DCC4]" data-id="element-238">
                    <item.icon className="w-10 h-10 text-[#8B5A2B]" data-id="element-239" />
                  </div>
                  <div data-id="element-240">
                    <h3 className="text-2xl font-serif text-[#2C1E16] mb-3 font-bold" data-id="element-241">
                      {item.title}
                    </h3>
                    <p className="text-xl text-[#5C4033] leading-relaxed font-light" data-id="element-242">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="mt-20" data-id="element-243">
              <CTAButton text="Quiero fortalecer mi cabello desde la raíz" data-id="element-244" />
            </div>
          </section>

          {/* THE 4 PROMISES (BENEFITS) */}
          <section className="py-20 border-t border-[#E8DCC4]" data-id="element-245">
            <h2 className="text-4xl md:text-6xl font-serif text-[#2C1E16] mb-16 text-center font-bold" data-id="element-246">
              ¿Qué sucede cuando tomas MINEvit?
            </h2>

            <div className="space-y-8" data-id="element-247">
              {[{
              title: 'Frena la caída del cabello',
              desc: 'Reduce visiblemente la caída fortaleciendo los folículos desde la raíz para que tu cabello se quede donde debe.',
              icon: ShieldCheck
            }, {
              title: 'Cabello más grueso y denso',
              desc: 'Estimula los folículos dormidos para devolverle cuerpo, volumen y densidad a tu melena.',
              icon: Activity
            }, {
              title: 'Crecimiento más rápido y fuerte',
              desc: 'Nutre el ciclo capilar para un crecimiento sano, largo y resistente a la quiebra.',
              icon: Zap
            }, {
              title: 'Pestañas y cejas más densas',
              desc: 'La misma nutrición folicular estimula pestañas y cejas visiblemente más pobladas.',
              icon: Eye
            }].map((benefit, i) => <div key={i} className="bg-white p-10 rounded-sm shadow-md border border-[#F4EFEA] flex flex-col md:flex-row items-center gap-8 text-center md:text-left" data-id="element-248">
                  <div className="bg-[#8B5A2B] w-24 h-24 rounded-full flex items-center justify-center shrink-0 shadow-lg" data-id="element-249">
                    <benefit.icon className="w-12 h-12 text-white" data-id="element-250" />
                  </div>
                  <div data-id="element-251">
                    <h3 className="text-3xl font-serif text-[#2C1E16] mb-4 font-bold" data-id="element-252">
                      {benefit.title}
                    </h3>
                    <p className="text-xl text-[#5C4033] leading-relaxed font-light" data-id="element-253">
                      {benefit.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </section>

          {/* FOUNDER / CEO STORY */}
          <section className="py-20 border-t border-[#E8DCC4]" data-id="element-254">
            <div className="relative overflow-hidden rounded-3xl bg-[#F4EFEA] border border-[#E8DCC4] shadow-lg" data-id="element-255">
              {/* Botanical spa accents */}
              <img src="https://images.unsplash.com/photo-1502691876148-a84978e59af8?auto=format&fit=crop&q=80&w=500&h=500" alt="" aria-hidden="true" onError={handleImgError} className="pointer-events-none absolute -top-16 -right-16 w-64 h-64 object-cover rounded-full opacity-20 mix-blend-multiply rotate-12" data-id="element-256" />
              <img src="https://images.unsplash.com/photo-1466781783364-36c955e42a7f?auto=format&fit=crop&q=80&w=500&h=500" alt="" aria-hidden="true" onError={handleImgError} className="pointer-events-none absolute -bottom-20 -left-16 w-64 h-64 object-cover rounded-full opacity-20 mix-blend-multiply -rotate-12" data-id="element-257" />

              <div className="relative grid md:grid-cols-5 gap-0 items-stretch" data-id="element-258">
                {/* Portrait */}
                <div className="md:col-span-2 relative min-h-[420px]" data-id="element-259">
                  <img src="https://cdn.magicpatterns.com/uploads/cj4gwAEKfVZNmJbj9RffVc/nataplaza.webp" alt="Laura Plaza, fundadora y CEO de MINEvit, sosteniendo la cápsula MINEvit" onError={handleImgError} className="absolute inset-0 w-full h-full object-cover object-top" data-id="element-260" />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-[#2C1E16]/40 to-transparent" data-id="element-261" />
                </div>

                {/* Story */}
                <div className="md:col-span-3 p-10 md:p-14 space-y-6" data-id="element-262">
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white text-[#8B5A2B] text-xs font-bold tracking-widest uppercase border border-[#E8DCC4]" data-id="element-263">
                    <Leaf className="w-3.5 h-3.5" data-id="element-264" />
                    <span data-id="element-265">De la fundadora</span>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-serif text-[#2C1E16] font-bold leading-tight" data-id="element-266">
                    Por qué creé MINEvit
                  </h2>
                  <p className="text-lg md:text-xl text-[#5C4033] leading-relaxed font-light" data-id="element-267">
                    MINEvit nació de una experiencia real—no de una tendencia,
                    sino de la búsqueda profunda de una solución cuando la caída
                    del cabello dejó de ser solo una preocupación estética y se
                    convirtió en algo personal.
                  </p>
                  <p className="text-lg md:text-xl text-[#5C4033] leading-relaxed font-light" data-id="element-268">
                    Tras experimentar la frustración de probar múltiples
                    alternativas sin resultados sostenibles, entendí que{' '}
                    <strong className="text-[#8B5A2B] font-medium italic" data-id="element-269">
                      el cabello no se repara desde afuera; se reconstruye desde
                      adentro.
                    </strong>
                  </p>
                  <p className="text-base font-bold text-[#2C1E16] uppercase tracking-widest pt-2" data-id="element-270">
                    — Laura Plaza, Fundadora & CEO de MINEvit
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* SOCIAL PROOF */}
          <section className="py-20" data-id="element-271">
            <div className="text-center mb-16" data-id="element-272">
              <div className="flex justify-center gap-2 mb-6" data-id="element-273">
                {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-8 h-8 text-[#8B5A2B] fill-[#8B5A2B]" data-id="element-274" />)}
              </div>
              <h2 className="text-4xl md:text-5xl font-serif text-[#2C1E16] font-bold" data-id="element-275">
                Con la confianza de miles de mujeres
              </h2>
            </div>

            <div className="space-y-8" data-id="element-276">
              {[{
              name: 'Sarah M.',
              text: 'Era escéptica porque había probado todas las gomitas para el cabello del mercado. En 4 semanas con MINEvit, mi caída se detuvo por completo y mi cabello se siente mucho más fuerte. Es un milagro.'
            }, {
              name: 'Elena R.',
              text: '¡Por fin un suplemento vegano que de verdad funciona! Mi cabello crece más rápido y lo noto muchísimo más grueso y con volumen. Voy por mi tercer frasco y nunca voy a dejar de tomarlo.'
            }, {
              name: 'Jessica T.',
              text: 'Ver cómo se rellenan mi línea del cabello y las entradas me devolvió por completo la confianza. ¡Gracias MINEvit!'
            }].map((testimonial, i) => <div key={i} className="bg-[#F4EFEA] p-10 rounded-sm border border-[#E8DCC4] relative" data-id="element-277">
                  <div className="flex gap-1 mb-4" data-id="element-278">
                    {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-5 h-5 text-[#8B5A2B] fill-[#8B5A2B]" data-id="element-279" />)}
                  </div>
                  <p className="text-xl text-[#2C1E16] font-serif italic mb-6 leading-relaxed" data-id="element-280">
                    "{testimonial.text}"
                  </p>
                  <p className="text-lg font-bold text-[#8B5A2B] uppercase tracking-wider" data-id="element-281">
                    — {testimonial.name}, Compradora Verificada
                  </p>
                </div>)}
            </div>

            <div className="mt-16" data-id="element-282">
              <CTAButton text="Quiero un cabello más denso y fuerte" data-id="element-283" />
            </div>
          </section>

          {/* OFFER STACK & GUARANTEE */}
          <section className="py-24 border-t-4 border-[#8B5A2B]" data-id="element-284">
            <div className="relative bg-white p-10 md:p-16 rounded-sm shadow-2xl border border-[#E8DCC4] overflow-hidden" data-id="element-285">
              <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#E8DCC4] blur-[100px] opacity-60" data-id="element-286" />
              <h2 className="text-4xl md:text-6xl font-serif text-[#2C1E16] mb-10 text-center font-bold" data-id="element-287">
                Esto es todo lo que recibes hoy:
              </h2>

              <div className="relative rounded-3xl overflow-hidden shadow-xl border-4 border-white mb-12 min-h-[200px]" data-id="element-288">
                <img src="https://cdn.magicpatterns.com/uploads/d4ig1SneTsSfVV29yk4F7b/minevitpart.webp" alt="Frasco de MINEvit dentro de un bolso, listo para llevar a diario" onError={handleImgError} className="w-full object-cover object-center aspect-[16/9]" data-id="element-289" />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#2C1E16]/40 via-transparent to-white/20" data-id="element-290" />
              </div>

              <ul className="space-y-6 text-2xl text-[#2C1E16] font-medium mb-12" data-id="element-291">
                <li className="flex items-center justify-between border-b border-[#F4EFEA] pb-4" data-id="element-292">
                  <span className="flex items-center gap-4" data-id="element-293">
                    <CheckCircle2 className="w-8 h-8 text-[#8B5A2B]" data-id="element-294" />{' '}
                    Suministro de MINEvit para 1 mes
                  </span>
                  <span className="text-[#8C7A6B] line-through" data-id="element-295">$85.00</span>
                </li>
                <li className="flex items-center justify-between border-b border-[#F4EFEA] pb-4" data-id="element-296">
                  <span className="flex items-center gap-4" data-id="element-297">
                    <CheckCircle2 className="w-8 h-8 text-[#8B5A2B]" data-id="element-298" /> Fórmula
                    celular avanzada
                  </span>
                  <span className="text-[#8C7A6B] line-through" data-id="element-299">$45.00</span>
                </li>
                <li className="flex items-center justify-between border-b border-[#F4EFEA] pb-4" data-id="element-300">
                  <span className="flex items-center gap-4" data-id="element-301">
                    <CheckCircle2 className="w-8 h-8 text-[#8B5A2B]" data-id="element-302" /> Envío
                    mundial exprés
                  </span>
                  <span className="text-[#8B5A2B] font-medium" data-id="element-303">$4.99</span>
                </li>
              </ul>

              <div className="text-center bg-[#FCFAF8] p-8 rounded-sm border border-[#E8DCC4] mb-12" data-id="element-304">
                <p className="text-2xl text-[#5C4033] mb-2 uppercase tracking-widest font-bold" data-id="element-305">
                  Valor total: <span className="line-through" data-id="element-306">$145.00</span>
                </p>
                <p className="text-xl text-[#2C1E16] mb-4" data-id="element-307">Tu precio hoy:</p>
                <p className="text-7xl font-serif font-bold text-[#8B5A2B] mb-4" data-id="element-308">
                  $65
                </p>
                <p className="text-red-700 font-bold animate-pulse text-lg" data-id="element-309">
                  ⚠️ Disponibilidad limitada
                </p>
              </div>

              {/* Guarantee */}
              <div className="flex flex-col md:flex-row items-center gap-8 bg-[#F4EFEA] p-8 rounded-sm border border-[#8B5A2B] mb-12" data-id="element-310">
                <ShieldCheck className="w-24 h-24 text-[#8B5A2B] shrink-0" data-id="element-311" />
                <div data-id="element-312">
                  <h3 className="text-2xl font-serif font-bold text-[#2C1E16] mb-2" data-id="element-313">
                    Garantía blindada de 60 días
                  </h3>
                  <p className="text-lg text-[#5C4033]" data-id="element-314">
                    Prueba MINEvit sin ningún riesgo. Si no ves un cabello más
                    denso, fuerte y con menos caída en 60 días, devuelve el
                    frasco vacío para un reembolso total. Sin preguntas.
                  </p>
                </div>
              </div>

              <CTAButton text="Quiero recuperar mi cabello por $65" subtext="Pago seguro • Envío en 24 horas" data-id="element-315" />
            </div>
          </section>

          {/* FAQ */}
          <section className="py-20" data-id="element-316">
            <h2 className="text-4xl font-serif text-[#2C1E16] mb-12 text-center font-bold" data-id="element-317">
              Preguntas frecuentes
            </h2>
            <div className="space-y-6" data-id="element-318">
              {[{
              q: '¿MINEvit es realmente 100% Vegano?',
              a: '¡Sí! No usamos ningún producto ni derivado animal. Nuestra fórmula es 100% vegana, libre de crueldad, sin lácteos y sin gluten.'
            }, {
              q: '¿Cuánto tardo en ver resultados?',
              a: 'El ciclo de crecimiento capilar toma tiempo. Muchas clientas notan menos caída en las primeras semanas, pero recomendamos tomar MINEvit de forma constante durante 60 días para ver resultados completos en densidad y fuerza.'
            }, {
              q: '¿Contiene hormonas?',
              a: 'Para nada. MINEvit es 100% libre de hormonas. Usamos compuestos naturales de origen vegetal para apoyar las propias rutas celulares de tu cuerpo.'
            }].map((faq, i) => <div key={i} className="bg-white p-8 rounded-sm shadow-sm border border-[#E8DCC4]" data-id="element-319">
                  <h3 className="text-2xl font-serif font-bold text-[#2C1E16] mb-4 flex items-start gap-3" data-id="element-320">
                    <ChevronDown className="w-6 h-6 text-[#8B5A2B] shrink-0 mt-1" data-id="element-321" />
                    {faq.q}
                  </h3>
                  <p className="text-xl text-[#5C4033] pl-9 leading-relaxed" data-id="element-322">
                    {faq.a}
                  </p>
                </div>)}
            </div>
          </section>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="relative overflow-hidden bg-gradient-to-b from-[#2C1E16] to-[#1A110B] text-[#8C7A6B] py-12 text-center" data-id="element-323">
        <div className="pointer-events-none absolute left-1/2 -top-20 -translate-x-1/2 w-[500px] h-[300px] rounded-full bg-[#8B5A2B] blur-[120px] opacity-20" data-id="element-324" />
        <div className="max-w-4xl mx-auto px-4 space-y-4" data-id="element-325">
          <p className="text-2xl font-serif font-bold text-[#E8DCC4] tracking-widest" data-id="element-326">
            MINE<span className="italic text-[#8B5A2B]" data-id="element-327">vit</span>
          </p>
          <p data-id="element-328">© 2026 MINEvit. Todos los derechos reservados.</p>
          <p className="text-sm max-w-2xl mx-auto" data-id="element-329">
            Estas afirmaciones no han sido evaluadas por la Administración de
            Alimentos y Medicamentos (FDA). Este producto no pretende
            diagnosticar, tratar, curar ni prevenir ninguna enfermedad.
          </p>
        </div>
      </footer>
    </div>;
}